/**
 * Multiformato de manifests: LEGACY_DIRECT vs ZIP_RANGE.
 * Canonicaliza keys/offsets del base (ingest) o URLs firmadas (presigner)
 * al contrato que consume loadStudyFromManifest / loadInstanceForMode.
 *
 * Regla crítica base no firmado:
 *   dicomKey con prefijo orthanc-internal/ → NUNCA inventar URL pública DIRECT;
 *   forzar estrategia RANGE_ZIP sobre downloadKey/archiveUrl.
 */

import {
  encodeObjectStoragePath,
  normalizeManifestShape,
} from './renderModeUrls'

export const MANIFEST_TYPE = {
  ZIP_RANGE: 'ZIP_RANGE',
  LEGACY_DIRECT: 'LEGACY_DIRECT',
}

export const DICOM_FETCH_TYPE = {
  RANGE_ZIP: 'RANGE_ZIP',
  DIRECT_URL: 'DIRECT_URL',
}

const ORTHANC_INTERNAL_PREFIX = 'orthanc-internal/'

/**
 * @param {string|null|undefined} key
 * @returns {boolean}
 */
export function isOrthancInternalKey(key) {
  if (key == null) return false
  const s = String(key)
    .trim()
    .replace(/^\/+/, '')
    .toLowerCase()
  return s.startsWith(ORTHANC_INTERNAL_PREFIX)
}

/**
 * @param {object|null|undefined} data
 * @returns {object|null}
 */
function firstInstance(data) {
  if (!data || typeof data !== 'object') return null
  if (Array.isArray(data.series)) {
    for (const series of data.series) {
      const instances = series && series.instances
      if (!Array.isArray(instances)) continue
      for (const inst of instances) {
        if (inst) return inst
      }
    }
  }
  if (Array.isArray(data.instances) && data.instances[0]) {
    return data.instances[0]
  }
  return null
}

/**
 * @param {object|null|undefined} data
 * @returns {'ZIP_RANGE'|'LEGACY_DIRECT'}
 */
export function detectManifestType(data) {
  if (!data || typeof data !== 'object') return MANIFEST_TYPE.LEGACY_DIRECT

  // Preview WebP-first (sin zip aún) sigue siendo familia ZIP_RANGE
  if (data.readiness === 'preview' || data.readiness === 'diagnostic') {
    return MANIFEST_TYPE.ZIP_RANGE
  }

  if (data.downloadKey || data.downloadUrl || data.archiveUrl) {
    const inst = firstInstance(data)
    if (
      inst &&
      (inst.byteOffset != null ||
        inst.zipEntryName ||
        inst.byteLength != null ||
        inst.webpKey ||
        inst.dicomKey)
    ) {
      return MANIFEST_TYPE.ZIP_RANGE
    }
    if (data.downloadKey) return MANIFEST_TYPE.ZIP_RANGE
  }

  const inst = firstInstance(data)
  if (inst && (inst.webpKey || (Array.isArray(inst.webpKeys) && inst.webpKeys.length))) {
    return MANIFEST_TYPE.ZIP_RANGE
  }
  if (
    inst &&
    inst.byteOffset != null &&
    inst.byteLength != null &&
    (inst.zipEntryName || data.downloadKey || data.downloadUrl)
  ) {
    return MANIFEST_TYPE.ZIP_RANGE
  }

  return MANIFEST_TYPE.LEGACY_DIRECT
}

/**
 * preview | diagnostic. Legacy sin campo: offsets+download → diagnostic; solo webp → preview.
 * WADO-URI / dcm4chee (url https por instancia, sin zip/webp) → diagnostic.
 * @param {object|null|undefined} data
 * @returns {'preview'|'diagnostic'}
 */
export function resolveManifestReadiness(data) {
  if (!data || typeof data !== 'object') return 'diagnostic'
  if (data.readiness === 'preview' || data.readiness === 'diagnostic') {
    return data.readiness
  }
  const hasDownload = !!(data.downloadKey || data.downloadUrl || data.archiveUrl)
  const inst = firstInstance(data)
  const hasOffsets =
    inst &&
    inst.byteOffset != null &&
    inst.byteLength != null &&
    Number(inst.byteLength) > 0
  if (hasDownload && hasOffsets) return 'diagnostic'
  if (inst && (inst.webpKey || (inst.webpKeys && inst.webpKeys.length))) {
    return 'preview'
  }
  // Manifest plataforma WADO-URI: instancias con url/dicomUrl http(s) = DICOM listo
  const dicomHttp =
    inst &&
    ((inst.url && /^https?:\/\//i.test(String(inst.url).trim())) ||
      (inst.dicomUrl && /^https?:\/\//i.test(String(inst.dicomUrl).trim())))
  if (dicomHttp) return 'diagnostic'
  return hasDownload ? 'diagnostic' : 'preview'
}

/**
 * Une assetBaseUrl + key relativa en URL http(s). No inventa firma.
 * @param {string|null} assetBaseUrl
 * @param {string|null|undefined} keyOrUrl
 * @returns {string|null}
 */
export function joinAssetUrl(assetBaseUrl, keyOrUrl) {
  if (keyOrUrl == null) return null
  const s = String(keyOrUrl).trim()
  if (!s) return null
  if (/^https?:\/\//i.test(s)) return s
  if (!assetBaseUrl) return null
  const baseNorm = String(assetBaseUrl).endsWith('/')
    ? String(assetBaseUrl)
    : `${assetBaseUrl}/`
  return `${baseNorm}${encodeObjectStoragePath(s)}`
}

/**
 * Serie/instancia informe fuera del study.zip (Path M / R2).
 * (series.storage === "loose" || series.role === "report")
 *   && (inst.url || inst.path || inst.key || inst.dicomUrl)
 * @param {object|null|undefined} series
 * @param {object|null|undefined} inst
 * @returns {boolean}
 */
export function isReportLooseSeriesInst(series, inst) {
  if (!inst || typeof inst !== 'object') return false
  const storage =
    (series && series.storage != null && String(series.storage).trim()) ||
    (inst.storage != null && String(inst.storage).trim()) ||
    ''
  const role =
    (series && series.role != null && String(series.role).trim()) ||
    (inst.role != null && String(inst.role).trim()) ||
    ''
  const isLooseOrReport =
    storage.toLowerCase() === 'loose' || role.toLowerCase() === 'report'
  if (!isLooseOrReport) return false
  const hasLoc =
    (inst.url != null && String(inst.url).trim()) ||
    (inst.path != null && String(inst.path).trim()) ||
    (inst.key != null && String(inst.key).trim()) ||
    (inst.dicomUrl != null && String(inst.dicomUrl).trim())
  return !!hasLoc
}

/**
 * Resuelve URL Part-10 de instancia report loose (url | dicomUrl | path | key).
 * @param {object} inst
 * @param {string|null} assetBaseUrl
 * @returns {string|null}
 */
export function resolveReportLooseUrl(inst, assetBaseUrl) {
  if (!inst || typeof inst !== 'object') return null
  const candidates = [inst.url, inst.dicomUrl, inst.path, inst.key]
  for (let i = 0; i < candidates.length; i++) {
    const joined = joinAssetUrl(assetBaseUrl, candidates[i])
    if (joined && /^https?:\/\//i.test(joined)) return joined
  }
  return null
}

/**
 * @param {object} inst
 * @param {string|null} assetBaseUrl
 * @param {string|null} archiveUrl
 */
function mapZipRangeInstance(inst, assetBaseUrl, archiveUrl) {
  const previewUrl =
    joinAssetUrl(assetBaseUrl, inst.previewUrl) ||
    joinAssetUrl(assetBaseUrl, inst.webpKey) ||
    null

  const byteOffset =
    inst.byteOffset != null && Number.isFinite(Number(inst.byteOffset))
      ? Number(inst.byteOffset)
      : null
  const byteLength =
    inst.byteLength != null && Number.isFinite(Number(inst.byteLength))
      ? Number(inst.byteLength)
      : null

  const hasRange =
    !!archiveUrl &&
    byteOffset != null &&
    byteOffset >= 0 &&
    byteLength != null &&
    byteLength > 0

  // dicomUrl / dicomKey → solo DIRECT resoluble (NUNCA orthanc-internal vía base pública)
  let dicomUrl = null
  const rawDicomUrl = inst.dicomUrl != null ? String(inst.dicomUrl).trim() : ''
  if (rawDicomUrl && /^https?:\/\//i.test(rawDicomUrl)) {
    dicomUrl = rawDicomUrl
  } else if (inst.dicomKey != null && String(inst.dicomKey).trim()) {
    const key = String(inst.dicomKey).trim()
    if (isOrthancInternalKey(key)) {
      dicomUrl = null
    } else if (/^https?:\/\//i.test(key)) {
      dicomUrl = key
    } else {
      dicomUrl = joinAssetUrl(assetBaseUrl, key)
    }
  }

  // Preferir RANGE_ZIP cuando hay zip+offsets (incluye base con dicomKey orthanc-internal)
  let dicomFetchType = DICOM_FETCH_TYPE.DIRECT_URL
  if (hasRange) {
    dicomFetchType = DICOM_FETCH_TYPE.RANGE_ZIP
  } else if (dicomUrl) {
    dicomFetchType = DICOM_FETCH_TYPE.DIRECT_URL
  }

  return {
    previewUrl,
    dicomUrl,
    archiveUrl: archiveUrl || null,
    byteOffset,
    byteLength,
    dicomFetchType,
  }
}

/**
 * Canonicaliza raw → contrato UDV.
 * @param {object} raw
 * @param {{ assetBaseUrl?: string|null, archiveUrlOverride?: string|null }} [opts]
 * @returns {object}
 */
export function canonicalizeManifest(raw, opts = {}) {
  const assetBaseUrl = opts.assetBaseUrl != null ? opts.assetBaseUrl : null
  const archiveOverride =
    opts.archiveUrlOverride != null && String(opts.archiveUrlOverride).trim()
      ? String(opts.archiveUrlOverride).trim()
      : null
  const shaped = normalizeManifestShape(raw)
  if (!shaped || typeof shaped !== 'object') {
    return shaped
  }

  const manifestType = detectManifestType(raw)

  const downloadUrl =
    archiveOverride ||
    joinAssetUrl(assetBaseUrl, shaped.downloadUrl) ||
    joinAssetUrl(assetBaseUrl, shaped.downloadKey) ||
    joinAssetUrl(assetBaseUrl, shaped.archiveUrl) ||
    null

  if (manifestType === MANIFEST_TYPE.LEGACY_DIRECT) {
    const series = Array.isArray(shaped.series)
      ? shaped.series.map((s) => {
          if (!s || !Array.isArray(s.instances)) return s
          return {
            ...s,
            instances: s.instances.map((inst) => {
              if (!inst) return inst
              return {
                ...inst,
                dicomFetchType: DICOM_FETCH_TYPE.DIRECT_URL,
                archiveUrl: null,
              }
            }),
          }
        })
      : shaped.series

    return {
      ...shaped,
      manifestType,
      readiness: resolveManifestReadiness(shaped),
      downloadUrl,
      series,
    }
  }

  // ZIP_RANGE (series imagen zip + reports storage:loose mezclados)
  const series = Array.isArray(shaped.series)
    ? shaped.series.map((s) => {
        if (!s || !Array.isArray(s.instances)) return s
        return {
          ...s,
          instances: s.instances.map((inst) => {
            if (!inst) return inst
            // Path M: informe Part-10 suelto — ignorar offsets/archiveUrl
            if (isReportLooseSeriesInst(s, inst)) {
              const looseUrl = resolveReportLooseUrl(inst, assetBaseUrl)
              const outLoose = {
                ...inst,
                dicomFetchType: DICOM_FETCH_TYPE.DIRECT_URL,
                dicomUrl: looseUrl,
                url: looseUrl || undefined,
                archiveUrl: null,
                byteOffset: null,
                byteLength: null,
                previewUrl:
                  joinAssetUrl(assetBaseUrl, inst.previewUrl) ||
                  joinAssetUrl(assetBaseUrl, inst.webpKey) ||
                  null,
              }
              return outLoose
            }
            const mapped = mapZipRangeInstance(inst, assetBaseUrl, downloadUrl)
            // Strip dicomKey orthanc-internal so resolvers legacy no lo interpretan como path
            const out = {
              ...inst,
              previewUrl: mapped.previewUrl,
              dicomUrl: mapped.dicomUrl,
              archiveUrl: mapped.archiveUrl,
              byteOffset: mapped.byteOffset,
              byteLength: mapped.byteLength,
              dicomFetchType: mapped.dicomFetchType,
              url:
                mapped.dicomUrl ||
                (inst.url && /^https?:\/\//i.test(String(inst.url))
                  ? String(inst.url).trim()
                  : undefined),
            }
            if (isOrthancInternalKey(inst.dicomKey)) {
              // Evitar path/key residuales en resolvers y el key privado
              delete out.key
              delete out.path
              delete out.dicomKey
            }
            return out
          }),
        }
      })
    : shaped.series

  return {
    ...shaped,
    manifestType,
    readiness: resolveManifestReadiness(shaped),
    downloadUrl,
    archiveUrl: downloadUrl,
    series,
  }
}

/**
 * ¿Instancia con DIAG vía range o URL directa?
 * @param {object} inst
 * @returns {boolean}
 */
export function instanceHasDiagnosticAsset(inst) {
  if (!inst || typeof inst !== 'object') return false
  if (
    inst.dicomFetchType === DICOM_FETCH_TYPE.RANGE_ZIP &&
    inst.archiveUrl &&
    inst.byteOffset != null &&
    inst.byteLength != null &&
    Number(inst.byteLength) > 0
  ) {
    return true
  }
  const d = inst.dicomUrl
  return d != null && /^https?:\/\//i.test(String(d).trim())
}

/**
 * @param {object} inst
 * @returns {boolean}
 */
export function instanceHasPreviewAsset(inst) {
  if (!inst || typeof inst !== 'object') return false
  const p = inst.previewUrl
  return p != null && String(p).trim().length > 0
}
